/**
 * Slider CSS Injector
 * Dynamically generates slider utility CSS based on detected classes in DOM
 * Replaces SCSS loops with runtime CSS generation
 *
 * Production ranges:
 * - Count: 2-15
 * - Width: 100-500
 */

class SliderCSSInjector {
  constructor() {
    this.breakpoints = this.getBreakpoints();
    this.detectedClasses = new Map(); // Map to avoid duplicates
    this.sliderElements = new Set(); // Track elements with slider classes
    this.styleTag = null;
  }

  /**
   * Read breakpoints from CSS custom properties
   * Reads from :root variables defined in _variables.scss
   */
  getBreakpoints() {
    const root = getComputedStyle(document.documentElement);
    return {
      'small-mobile': parseInt(root.getPropertyValue('--mad-breakpoint-small-mobile')) || 480,
      'mobile': parseInt(root.getPropertyValue('--mad-breakpoint-mobile')) || 768,
      'tablet': parseInt(root.getPropertyValue('--mad-breakpoint-tablet')) || 991,
      'desktop': parseInt(root.getPropertyValue('--mad-breakpoint-desktop')) || 1200,
      'large-desktop': parseInt(root.getPropertyValue('--mad-breakpoint-large-desktop')) || 1600
    };
  }

  /**
   * Scan DOM for slider utility classes
   * Matches: {prefix}-slider-{type}-{value}
   * Where:
   * - prefix: '', 'tablet', 'mobile', 'small-mobile', 'mad-mobile', 'desktop', 'large-desktop'
   * - type: 'count'/'c' or 'width'/'w'
   * - value: 2-15 for count, 100-500 for width
   */
  scanDOM() {
    const elements = document.querySelectorAll('[class*="slider"]');

    elements.forEach(el => {
      const classes = el.className.split(/\s+/);
      let hasSliderClass = false;

      classes.forEach(cls => {
        // Check for base slider classes: slider, tablet-slider, mobile-slider, etc.
        const baseSliderPattern = /^(.*?-)?slider$/;
        if (baseSliderPattern.test(cls)) {
          hasSliderClass = true;
        }

        // Match slider count classes with mixed separator patterns
        // Examples: tablet-slider-count-5, mobile-slider__count-3, slider-c-2
        // Note: Supports mixed separators like mobile-slider__count-5 (__ then -)
        const countPattern = /^(.*?)slider([-_]{1,2})(count|c)([-_]{1,2})(\d+)$/;
        const widthPattern = /^(.*?)slider([-_]{1,2})(width|w)([-_]{1,2})(\d+)$/;

        let match = cls.match(countPattern);
        if (match) {
          const prefix = match[1].replace(/-$/, ''); // Remove trailing dash
          const separator1 = match[2]; // Separator between slider and type
          const variant = match[3];
          const separator2 = match[4]; // Separator between type and value
          const value = parseInt(match[5]);

          if (value >= 2 && value <= 15) {
            const key = `${prefix}|count|${separator1}|${separator2}|${variant}|${value}`;
            this.detectedClasses.set(key, { prefix, separator1, separator2, type: 'count', variant, value });
            hasSliderClass = true;
          }
        }

        match = cls.match(widthPattern);
        if (match) {
          const prefix = match[1].replace(/-$/, '');
          const separator1 = match[2]; // Separator between slider and type
          const variant = match[3];
          const separator2 = match[4]; // Separator between type and value
          const value = parseInt(match[5]);

          if (value >= 100 && value <= 500) {
            const key = `${prefix}|width|${separator1}|${separator2}|${variant}|${value}`;
            this.detectedClasses.set(key, { prefix, separator1, separator2, type: 'width', variant, value });
            hasSliderClass = true;
          }
        }
      });

      if (hasSliderClass) {
        this.sliderElements.add(el);
      }
    });
  }

  /**
   * Generate complete CSS for all detected classes
   */
  generateCSS() {
    if (this.detectedClasses.size === 0) {
      return '';
    }

    let css = '/* Mad Slider Utilities - Dynamically Generated */\n';

    // Group classes by prefix for efficient media queries
    const byPrefix = new Map();

    this.detectedClasses.forEach((classData) => {
      const { prefix } = classData;
      if (!byPrefix.has(prefix)) {
        byPrefix.set(prefix, []);
      }
      byPrefix.get(prefix).push(classData);
    });

    // Generate CSS for each prefix group
    byPrefix.forEach((classes, prefix) => {
      const mediaQuery = this.getMediaQuery(prefix);

      // Open media query if needed
      if (mediaQuery) {
        css += `\n@media ${mediaQuery} {\n`;
      }

      // Add base slider styles for this prefix (inside media query)
      css += this.generateBaseSliderStylesForPrefix(prefix);

      // Add specific count/width classes
      classes.forEach((classData) => {
        css += this.generateSingleClassCSS(classData);
      });

      // Close media query if needed
      if (mediaQuery) {
        css += '}\n';
      }
    });

    return css;
  }

  /**
   * Generate base slider styles for a specific prefix
   * These match the SCSS .#{$prefix}slider base styles
   */
  generateBaseSliderStylesForPrefix(prefix) {
    let css = '';
    const className = prefix ? `${prefix}-slider` : 'slider';

    css += `  .${className} {
    width: 100%;
    overflow-x: scroll;
    overflow-y: hidden;
    justify-content: flex-start !important;
  }\n`;

    css += `  .${className} > * {
    display: flex;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    gap: 8px;
  }\n`;

    // Add padding for mobile/tablet
    if (prefix === 'tablet' || prefix === 'mobile' || prefix === 'small-mobile' ||
        prefix === 'mad-mobile' || prefix === 'mad-small-mobile') {
      css += `  .${className} > * {
    padding: 0 15px;
  }\n`;
    }

    // Add scrollbar hiding for non-editor views
    css += `  body > *:not(#brz-ed-root) .${className} {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }\n`;

    css += `  body > *:not(#brz-ed-root) .${className}::-webkit-scrollbar {
    display: none;
  }\n`;

    return css;
  }

  /**
   * Get media query string for a given prefix
   * Matches the media query ranges from SCSS exactly
   */
  getMediaQuery(prefix) {
    const queries = {
      '': null, // No media query for base
      'small-mobile': `(max-width: ${this.breakpoints['small-mobile']}px)`,
      'mad-small-mobile': `(max-width: ${this.breakpoints['small-mobile']}px)`,
      'mobile': `(max-width: ${this.breakpoints['mobile']}px)`,
      'mad-mobile': `(max-width: ${this.breakpoints['mobile']}px)`,
      'tablet': `(min-width: ${this.breakpoints['mobile'] + 1}px) and (max-width: ${this.breakpoints['tablet']}px)`,
      'desktop': `(min-width: ${this.breakpoints['tablet'] + 1}px) and (max-width: ${this.breakpoints['desktop']}px)`,
      'large-desktop': `(min-width: ${this.breakpoints['desktop'] + 1}px) and (max-width: ${this.breakpoints['large-desktop']}px)`
    };

    return queries[prefix] || null;
  }

  /**
   * Generate CSS for a single slider utility class
   */
  generateSingleClassCSS({ prefix, separator1, separator2, type, variant, value }) {
    const prefixStr = prefix ? `${prefix}-` : '';
    const className = `${prefixStr}slider${separator1}${variant}${separator2}${value}`;

    if (type === 'count') {
      // Count classes: flex-basis percentage
      const percentage = (100 / value).toFixed(10);
      return `  .${className} > * > * { flex: 1 1 ${percentage}% !important; }\n`;
    } else {
      // Width classes: min-width viewport units
      const minWidth = prefix === 'tablet'
        ? `${value}vw`
        : `calc(${value}vw + 30px)`;
      return `  .${className} > * { min-width: ${minWidth}; }\n`;
    }
  }

  /**
   * Inject generated CSS into document head
   */
  injectCSS(css) {
    if (!css) {
      return;
    }

    if (!this.styleTag) {
      this.styleTag = document.createElement('style');
      this.styleTag.id = 'mad-slider-utilities-dynamic';
      this.styleTag.setAttribute('data-source', 'slider-css-injector');
      this.styleTag.setAttribute('data-no-optimize', '1');
      document.head.appendChild(this.styleTag);
    }

    this.styleTag.textContent = css;

    // Mark slider elements as initialized
    this.markAsInitialized();
  }

  /**
   * Add 'initialized' class to all detected slider elements
   */
  markAsInitialized() {
    this.sliderElements.forEach(el => {
      el.classList.add('initialized');
    });
  }

  /**
   * Main execution method
   */
  run() {
    this.scanDOM();

    if (this.detectedClasses.size > 0) {
      const css = this.generateCSS();
      this.injectCSS(css);
    }
  }
}

// Auto-initialize on DOM ready
function initSliderInjector() {
  const injector = new SliderCSSInjector();
  injector.run();
}

initSliderInjector();

// Export using CommonJS syntax
module.exports = SliderCSSInjector;
